import { useEffect, useRef } from "react";
import { analytics, crashlytics } from "../services/firebase";
import { useAuth } from "../contexts/AuthContext";

/**
 * Hook to track page views and time spent on a page
 * @param pageName - Name of the page (e.g., 'ActivityPage', 'BrowsePage')
 * @param pageClass - Class/component name for the page
 */
export const usePageTracking = (
  pageName: string,
  pageClass: string,
) => {
  const startTimeRef = useRef<number>(Date.now());
  const { currentUser, isLoading: authLoading } = useAuth();

  useEffect(() => {
    // Track page view on mount
    startTimeRef.current = Date.now();
    analytics.setCurrentScreen(pageName, pageClass);
    analytics.logEvent(`wii_${pageName}`, {
      page_name: pageName,
      page_class: pageClass,
      userId: authLoading || !currentUser ? "unavailable" : currentUser?.id,
      userName:
        authLoading || !currentUser ? "unavailable" : currentUser?.username,
      timestamp: new Date().toISOString(),
    });
    crashlytics.log(
      `[${currentUser?.id || "unavailable"}|${currentUser?.username || "unavailable"}] Navigated to ${pageName} at ${new Date().toISOString()}`,
    );

    // Track time spent on unmount
    return () => {
      const timeSpent = Math.round((Date.now() - startTimeRef.current) / 1000); // in seconds
      analytics.logEvent(`wii_${pageName}_time_spent`, {
        page_name: pageName,
        page_class: pageClass,
        userId: authLoading || !currentUser ? "unavailable" : currentUser?.id,
        userName:
          authLoading || !currentUser ? "unavailable" : currentUser?.username,
        duration_seconds: timeSpent,
        timestamp: new Date().toISOString(),
      });
      crashlytics.log(
        `[${currentUser?.id || "unavailable"}|${currentUser?.username || "unavailable"}] Left ${pageName} after ${timeSpent} seconds at ${new Date().toISOString()}`,
      );
    };
  }, [pageName, pageClass]);
};
